<script setup>
/* global axios */
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons';

import Button from 'dashboard/components-next/button/Button.vue';
import CompanyCustomAttributes from 'dashboard/components-next/Companies/CompanyDetail/CompanyCustomAttributes.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import SocialProfileInput from 'dashboard/components-next/social-profiles/SocialProfileInput.vue';
import {
  socialProfileHandle,
  socialProfileUrl,
} from 'dashboard/components-next/social-profiles/socialProfiles';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useCompaniesStore } from 'dashboard/stores/companies';

const props = defineProps({
  company: { type: Object, required: true },
});

const DESCRIPTION_MAX_LENGTH = 1000;
const SOCIAL_NETWORKS = [
  'linkedin',
  'x',
  'facebook',
  'instagram',
  'github',
  'youtube',
];

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const companiesStore = useCompaniesStore();
const companyAttributes = useMapGetter('attributes/getCompanyAttributes');
const panelRef = ref(null);

const form = reactive({
  name: '',
  domain: '',
  description: '',
  cnpj: '',
  industry: '',
  employees: '',
  city: '',
  country: '',
  phone: '',
  socialProfiles: {},
});
const initialDetails = ref({});

// --- Consulta de CNPJ (BrasilAPI + cache nativo; ver Kanban::CnpjClient) ---
const cnpjLoading = ref(false);
const cnpjError = ref('');
const cnpjResult = ref(null);
const enriquecido = ref(false);

const soDigitos = v => String(v || '').replace(/[^0-9A-Za-z]/g, '');
const cnpjValido = computed(() => soDigitos(form.cnpj).length === 14);

// xx.xxx.xxx/xxxx-xx (só quando numérico de 14)
const formatarCnpj = v => {
  const s = soDigitos(v);
  if (s.length !== 14 || /[A-Za-z]/.test(s)) return s;
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`;
};

const formatarTelefone = d => {
  const s = String(d || '').replace(/\D/g, '');
  if (s.length < 10) return '';
  const ddd = s.slice(0, 2);
  const resto = s.slice(2);
  const meio = resto.length > 8 ? 5 : 4;
  return `(${ddd}) ${resto.slice(0, meio)}-${resto.slice(meio)}`;
};

const situacaoAtiva = computed(
  () =>
    String(cnpjResult.value?.descricao_situacao_cadastral || '').toUpperCase() ===
    'ATIVA'
);

// Passo 1: busca na BrasilAPI e só MOSTRA o resultado (não altera o cadastro ainda).
const consultarCnpj = async () => {
  const num = soDigitos(form.cnpj);
  if (num.length !== 14) {
    cnpjError.value = t('COMPANIES.DETAIL.EDIT.CNPJ.INVALID');
    return;
  }
  cnpjLoading.value = true;
  cnpjError.value = '';
  enriquecido.value = false;
  try {
    const accountId = route.params.accountId;
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/kanban/cnpj/${num}`
    );
    cnpjResult.value = data.data;
    form.cnpj = formatarCnpj(data.data.cnpj || num);
  } catch (e) {
    cnpjResult.value = null;
    cnpjError.value =
      e?.response?.status === 404
        ? t('COMPANIES.DETAIL.EDIT.CNPJ.NOT_FOUND')
        : e?.response?.data?.error || t('COMPANIES.DETAIL.EDIT.CNPJ.ERROR');
  } finally {
    cnpjLoading.value = false;
  }
};

// Passo 2 (botão explícito): preenche os campos da empresa com os dados oficiais.
const enriquecerCadastro = () => {
  const d = cnpjResult.value;
  if (!d) return;

  const nome = d.nome_fantasia || d.razao_social;
  if (nome) form.name = nome;
  if (d.cnae_fiscal_descricao) form.industry = d.cnae_fiscal_descricao;
  if (d.municipio) form.city = d.municipio;
  form.country = 'Brasil';
  const tel = formatarTelefone(d.ddd_telefone_1);
  if (tel) form.phone = tel;
  // Descrição só se estiver vazia (não sobrescreve o que o usuário escreveu).
  if (!form.description.trim()) {
    const resumo = [
      d.razao_social,
      d.natureza_juridica,
      d.porte ? `Porte: ${d.porte}` : null,
    ].filter(Boolean);
    if (resumo.length) form.description = resumo.join(' · ');
  }

  enriquecido.value = true;
  useAlert(t('COMPANIES.DETAIL.EDIT.CNPJ.ENRICHED'));
};

// ---- Sugestões de dados adicionais (viram atributos personalizados da empresa) ----
const formatarMoeda = v => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return null;
  try {
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch (e) {
    return `R$ ${n}`;
  }
};

const formatarDataBR = v => {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};

const enderecoCompleto = d => {
  const linha1 = [d.descricao_tipo_de_logradouro, d.logradouro, d.numero]
    .filter(Boolean)
    .join(' ');
  return [
    [linha1, d.complemento].filter(Boolean).join(', '),
    d.bairro,
    [d.municipio, d.uf].filter(Boolean).join(' - '),
    d.cep ? `CEP ${d.cep}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

const simOuNao = v => (v === true ? true : v === false ? false : null);

// type: 0=text, 5=date (usamos texto p/ robustez), 7=checkbox
const EXTRA_DEFS = [
  {
    key: 'inscricao_estadual',
    name: 'Inscrição estadual',
    type: 0,
    get: d => d.inscricao_estadual,
  },
  { key: 'email', name: 'E-mail da empresa', type: 0, get: d => d.email },
  { key: 'endereco', name: 'Endereço', type: 0, get: d => enderecoCompleto(d) },
  { key: 'cep', name: 'CEP', type: 0, get: d => d.cep },
  {
    key: 'natureza_juridica',
    name: 'Natureza jurídica',
    type: 0,
    get: d => d.natureza_juridica,
  },
  { key: 'porte', name: 'Porte', type: 0, get: d => d.porte },
  {
    key: 'capital_social',
    name: 'Capital social',
    type: 0,
    get: d => formatarMoeda(d.capital_social),
  },
  {
    key: 'data_abertura',
    name: 'Data de abertura',
    type: 0,
    get: d => formatarDataBR(d.data_inicio_atividade),
  },
  {
    key: 'telefone_secundario',
    name: 'Telefone secundário',
    type: 0,
    get: d => formatarTelefone(d.ddd_telefone_2),
  },
  {
    key: 'socios',
    name: 'Sócios',
    type: 0,
    get: d =>
      (d.qsa || [])
        .map(s => s.nome_socio)
        .filter(Boolean)
        .join(', '),
  },
  {
    key: 'simples_nacional',
    name: 'Simples Nacional',
    type: 7,
    get: d => simOuNao(d.opcao_pelo_simples),
  },
  { key: 'mei', name: 'MEI', type: 7, get: d => simOuNao(d.opcao_pelo_mei) },
];

const selecionados = reactive({});
const aplicandoSugestoes = ref(false);

// Dados que a BrasilAPI trouxe e que ainda NÃO estão no cadastro: viram sugestões.
const sugestoes = computed(() => {
  const d = cnpjResult.value;
  if (!d) return [];
  const defs = companyAttributes.value || [];
  const atuais = props.company.customAttributes || {};
  return EXTRA_DEFS.map(def => {
    const value = def.get(d);
    if (value === null || value === undefined || value === '') return null;
    // casa com um atributo já existente (pela chave ou pelo nome)
    const existing = defs.find(
      a =>
        a.attributeKey === def.key ||
        convertToAttributeSlug(a.attributeDisplayName || '') === def.key
    );
    const chaveFinal = existing?.attributeKey || def.key;
    // já preenchido nesta empresa? então não sugere.
    if (atuais[chaveFinal] !== undefined && atuais[chaveFinal] !== null)
      return null;
    return { ...def, value, existingKey: existing?.attributeKey || null };
  }).filter(Boolean);
});

const algumSelecionado = computed(() =>
  sugestoes.value.some(s => selecionados[s.key])
);

const valorSugestao = s =>
  s.type === 7 ? (s.value ? t('COMPANIES.DETAIL.FACTS.YES') : t('COMPANIES.DETAIL.FACTS.NO')) : s.value;

// marca tudo como selecionado por padrão quando surgem novas sugestões
watch(
  sugestoes,
  list => {
    list.forEach(s => {
      if (selecionados[s.key] === undefined) selecionados[s.key] = true;
    });
  },
  { immediate: true }
);

const aplicarSugestoes = async () => {
  const sel = sugestoes.value.filter(s => selecionados[s.key]);
  if (!sel.length) return;
  aplicandoSugestoes.value = true;
  try {
    const custom = {};
    // cria os atributos que faltam (nível conta) e monta os valores
    for (const s of sel) {
      let chave = s.existingKey;
      if (!chave) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await store.dispatch('attributes/create', {
            attribute_display_name: s.name,
            attribute_description: t('COMPANIES.DETAIL.EDIT.CNPJ.ATTR_DESC'),
            attribute_model: 2,
            attribute_display_type: s.type,
            attribute_key: s.key,
            attribute_values: [],
          });
        } catch (err) {
          // provavelmente já existe (uniqueness) — segue usando a chave
        }
        chave = s.key;
      }
      custom[chave] = s.value;
    }
    await companiesStore.update({
      id: props.company.id,
      customAttributes: custom,
    });
    await store.dispatch('attributes/get');
    useAlert(t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_APPLIED'));
  } catch (e) {
    useAlert(e.message || t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_ERROR'));
  } finally {
    aplicandoSugestoes.value = false;
  }
};

const isUpdating = computed(() => companiesStore.getUIFlags.updatingItem);
const isFormInvalid = computed(() => !form.name.trim());

const socialLabels = computed(() => ({
  linkedin: t('COMPANIES.DETAIL.EDIT.SOCIAL.LINKEDIN'),
  x: t('COMPANIES.DETAIL.EDIT.SOCIAL.X'),
  facebook: t('COMPANIES.DETAIL.EDIT.SOCIAL.FACEBOOK'),
  instagram: t('COMPANIES.DETAIL.EDIT.SOCIAL.INSTAGRAM'),
  github: t('COMPANIES.DETAIL.EDIT.SOCIAL.GITHUB'),
  youtube: t('COMPANIES.DETAIL.EDIT.SOCIAL.YOUTUBE'),
}));

// Editing industry or employees replaces the enriched value shown and clears its counterpart.
const readDetails = attributes => ({
  industry: attributes.sub_industry || attributes.industry || '',
  employees: String(
    attributes.employee_count || attributes.employee_count_range || ''
  ),
  city: attributes.city || '',
  country: attributes.country || '',
  phone: attributes.phone || '',
});

const open = () => {
  const attributes = props.company.additionalAttributes || {};
  const socialProfiles = attributes.social_profiles || {};
  initialDetails.value = readDetails(attributes);
  cnpjError.value = '';
  cnpjResult.value = null;
  enriquecido.value = false;
  Object.keys(selecionados).forEach(k => delete selecionados[k]);
  store.dispatch('attributes/get');

  Object.assign(form, {
    name: props.company.name || '',
    domain: props.company.domain || '',
    description: props.company.description || '',
    cnpj: attributes.cnpj || '',
    ...initialDetails.value,
    socialProfiles: {
      ...Object.fromEntries(
        SOCIAL_NETWORKS.map(network => [
          network,
          socialProfileHandle(network, socialProfiles[network]),
        ])
      ),
      x: socialProfileHandle('x', socialProfiles.x || socialProfiles.twitter),
    },
  });
  panelRef.value?.open();
};

const close = () => panelRef.value?.close();

const changed = field =>
  form[field].trim() !== initialDetails.value[field].trim();

const buildAdditionalAttributes = () => {
  const attributes = { ...(props.company.additionalAttributes || {}) };
  const assign = (key, value) => {
    if (value) attributes[key] = value;
    else delete attributes[key];
  };

  if (changed('industry')) {
    assign('industry', form.industry.trim());
    delete attributes.sub_industry;
  }
  if (changed('employees')) {
    assign('employee_count_range', form.employees.trim());
    delete attributes.employee_count;
  }
  if (changed('country')) delete attributes.country_code;
  assign('city', form.city.trim());
  assign('country', form.country.trim());
  assign('phone', form.phone.trim());

  const socialProfiles = { ...attributes.social_profiles };
  delete socialProfiles.twitter;
  Object.entries(form.socialProfiles).forEach(([network, handle]) => {
    const url = socialProfileUrl(network, handle);
    if (url) socialProfiles[network] = url;
    else delete socialProfiles[network];
  });
  delete attributes.social_profiles;
  if (Object.keys(socialProfiles).length) {
    attributes.social_profiles = socialProfiles;
  }

  // CNPJ: guarda o número formatado + um subconjunto útil dos dados da BrasilAPI
  // (consultado nesta edição). Se o campo foi limpo, remove o cnpj.
  const cnpjNum = soDigitos(form.cnpj);
  if (cnpjNum.length === 14) {
    attributes.cnpj = formatarCnpj(form.cnpj);
    const d = cnpjResult.value;
    if (d) {
      attributes.cnpj_razao_social = d.razao_social || attributes.cnpj_razao_social;
      attributes.cnpj_nome_fantasia =
        d.nome_fantasia || attributes.cnpj_nome_fantasia;
      attributes.cnpj_situacao =
        d.descricao_situacao_cadastral || attributes.cnpj_situacao;
      attributes.cnpj_natureza =
        d.natureza_juridica || attributes.cnpj_natureza;
      attributes.cnpj_porte = d.porte || attributes.cnpj_porte;
      attributes.cnpj_abertura =
        d.data_inicio_atividade || attributes.cnpj_abertura;
    }
  } else if (!cnpjNum.length) {
    delete attributes.cnpj;
  }

  return attributes;
};

const handleConfirm = async () => {
  if (isFormInvalid.value) return;

  try {
    await companiesStore.update({
      id: props.company.id,
      name: form.name.trim(),
      domain: form.domain.trim() || null,
      description: form.description.trim() || null,
      additionalAttributes: buildAdditionalAttributes(),
    });
    useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_SUCCESS'));
    close();
  } catch (error) {
    useAlert(
      error.message || t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_ERROR')
    );
  }
};

defineExpose({ open });
</script>

<template>
  <SidePanel
    ref="panelRef"
    width="2xl"
    :title="t('COMPANIES.DETAIL.EDIT.TITLE')"
  >
    <div class="flex flex-col gap-8">
      <section class="flex flex-col gap-4">
        <!-- Consulta de CNPJ (BrasilAPI): preenche os campos da empresa -->
        <div
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <label class="text-label text-n-slate-12">
            {{ t('COMPANIES.DETAIL.EDIT.CNPJ.LABEL') }}
          </label>
          <div class="flex items-start gap-2">
            <div class="flex-1">
              <Input
                v-model="form.cnpj"
                :placeholder="t('COMPANIES.DETAIL.EDIT.CNPJ.PLACEHOLDER')"
                :disabled="isUpdating || cnpjLoading"
                :message="cnpjError"
                :message-type="cnpjError ? 'error' : 'info'"
              />
            </div>
            <Button
              :label="
                cnpjLoading
                  ? t('COMPANIES.DETAIL.EDIT.CNPJ.LOADING')
                  : t('COMPANIES.DETAIL.EDIT.CNPJ.LOOKUP')
              "
              color="blue"
              variant="outline"
              type="button"
              :is-loading="cnpjLoading"
              :disabled="!cnpjValido || isUpdating || cnpjLoading"
              @click="consultarCnpj"
            />
          </div>
          <template v-if="cnpjResult">
            <p class="text-sm text-n-slate-11">
              <span class="font-medium text-n-slate-12">{{
                cnpjResult.razao_social
              }}</span>
              <span
                class="ml-1 px-1.5 py-0.5 rounded text-[0.6875rem] font-medium"
                :class="
                  situacaoAtiva
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
                >{{ cnpjResult.descricao_situacao_cadastral }}</span
              >
            </p>
            <div class="flex items-center gap-2">
              <Button
                :label="t('COMPANIES.DETAIL.EDIT.CNPJ.ENRICH')"
                color="blue"
                size="sm"
                type="button"
                :disabled="isUpdating"
                @click="enriquecerCadastro"
              />
              <span
                v-if="enriquecido"
                class="inline-flex items-center gap-1 text-xs text-n-teal-11"
              >
                <span class="i-lucide-check size-3.5" />
                {{ t('COMPANIES.DETAIL.EDIT.CNPJ.ENRICHED') }}
              </span>
            </div>

            <!-- Dados adicionais encontrados → viram atributos personalizados -->
            <div
              v-if="sugestoes.length"
              class="flex flex-col gap-1.5 pt-2 mt-1 border-t border-n-weak"
            >
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_TITLE') }}
              </p>
              <label
                v-for="s in sugestoes"
                :key="s.key"
                class="flex items-start gap-2 text-xs cursor-pointer"
              >
                <input
                  v-model="selecionados[s.key]"
                  type="checkbox"
                  class="mt-0.5 accent-n-brand"
                />
                <span class="min-w-0">
                  <span class="text-n-slate-12">{{ s.name }}:</span>
                  <span class="text-n-slate-11"> {{ valorSugestao(s) }}</span>
                  <span
                    class="ml-1 px-1 rounded text-[0.625rem] align-middle"
                    :class="
                      s.existingKey
                        ? 'bg-n-slate-3 text-n-slate-10'
                        : 'bg-n-teal-3 text-n-teal-11'
                    "
                  >
                    {{
                      s.existingKey
                        ? t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_EXISTING')
                        : t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_NEW')
                    }}
                  </span>
                </span>
              </label>
              <div class="mt-1">
                <Button
                  :label="t('COMPANIES.DETAIL.EDIT.CNPJ.SUGGEST_APPLY')"
                  color="blue"
                  variant="outline"
                  size="sm"
                  type="button"
                  :is-loading="aplicandoSugestoes"
                  :disabled="aplicandoSugestoes || !algumSelecionado"
                  @click="aplicarSugestoes"
                />
              </div>
            </div>
          </template>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('COMPANIES.DETAIL.EDIT.CNPJ.HINT') }}
          </p>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.name"
            :label="t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME')"
            :disabled="isUpdating"
            autofocus
          />
          <Input
            v-model="form.domain"
            :label="t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN')"
            :placeholder="t('COMPANIES.DETAIL.EDIT.PLACEHOLDERS.DOMAIN')"
            :disabled="isUpdating"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-label text-n-slate-12">
            {{ t('COMPANIES.DETAIL.EDIT.DESCRIPTION') }}
          </label>
          <TextArea
            v-model="form.description"
            :placeholder="t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')"
            :disabled="isUpdating"
            :max-length="DESCRIPTION_MAX_LENGTH"
            class="w-full"
            show-character-count
            auto-height
          />
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h4 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('COMPANIES.DETAIL.EDIT.DETAILS') }}
        </h4>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.industry"
            :label="t('COMPANIES.DETAIL.FACTS.INDUSTRY')"
            :placeholder="t('COMPANIES.DETAIL.EDIT.PLACEHOLDERS.INDUSTRY')"
            :disabled="isUpdating"
          />
          <Input
            v-model="form.employees"
            :label="t('COMPANIES.DETAIL.FACTS.SIZE')"
            :placeholder="t('COMPANIES.DETAIL.EDIT.PLACEHOLDERS.EMPLOYEES')"
            :disabled="isUpdating"
          />
          <Input
            v-model="form.city"
            :label="t('COMPANIES.DETAIL.EDIT.CITY')"
            :disabled="isUpdating"
          />
          <Input
            v-model="form.country"
            :label="t('COMPANIES.DETAIL.EDIT.COUNTRY')"
            :disabled="isUpdating"
          />
          <Input
            v-model="form.phone"
            :label="t('COMPANIES.DETAIL.EDIT.PHONE')"
            type="tel"
            :disabled="isUpdating"
          />
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h4 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('COMPANIES.DETAIL.EDIT.SOCIAL.TITLE') }}
        </h4>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SocialProfileInput
            v-for="network in SOCIAL_NETWORKS"
            :key="network"
            v-model="form.socialProfiles[network]"
            :network="network"
            :label="socialLabels[network]"
            :placeholder="t('COMPANIES.DETAIL.EDIT.PLACEHOLDERS.USERNAME')"
            :disabled="isUpdating"
          />
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h4 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('COMPANIES.DETAIL.ATTRIBUTES.TITLE') }}
        </h4>
        <CompanyCustomAttributes :company="company" />
      </section>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          type="button"
          @click="close"
        />
        <Button
          :label="t('COMPANIES.DETAIL.EDIT.SAVE')"
          color="blue"
          type="button"
          :disabled="isFormInvalid || isUpdating"
          :is-loading="isUpdating"
          @click="handleConfirm"
        />
      </div>
    </template>
  </SidePanel>
</template>
